import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axiosInstance from '../config/axios';
import { getFriendlyErrorMessage } from '../utils/errorMessages';
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/auth/me');
      setUser(response.data.user);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axiosInstance.post('/api/auth/change-password', {
        currentPassword,
        newPassword
      });
      // Refresh user data after password change
      await fetchUser();
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Change password error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to change password'
      };
    }
  };

  useEffect(() => {
    if (token) {
      if (user) {
        setLoading(false);
      } else {
        fetchUser();
      }
    } else {
      setLoading(false);
    }
  }, [token, user, fetchUser]);

  const login = async (email, password) => {
    try {
      const loginUrl = typeof window !== 'undefined' && window.location.origin
        ? `${window.location.origin}/api/auth/login`
        : '/api/auth/login';
      const headers = { 'Content-Type': 'application/json' };
      if (typeof window !== 'undefined' && window.location.host) {
        headers['X-Tenant-Host'] = window.location.host;
      }
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        return {
          success: false,
          error: getFriendlyErrorMessage(
            { response: { status: response.status, data } },
            { kind: 'login' }
          ),
        };
      }
      const { token: newToken, user: userData, mustChangePassword } = data;
      setToken(newToken);
      const userWithFlag = { ...userData, mustChangePassword };
      setUser(userWithFlag);
      localStorage.setItem('token', newToken);
      return { success: true, mustChangePassword: mustChangePassword || false, user: userWithFlag };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Login failed. Please check your connection and try again.'
      };
    }
  };


  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    changePassword
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
